/**
 * Triggers Service - Irrigo Platform
 * Servicio para gestión de triggers automáticos
 */

import { api, ApiResponse, PaginatedResponse, PaginationParams } from './api';
import type { 
  ApiTrigger, 
  CreateTriggerRequest, 
  UpdateTriggerRequest,
  AssignTriggerZonesRequest,
  ApiTriggerLinkedZone,
  ApiAffectedControllersResponse,
} from '@/types/api';

// ============================================================================
// TYPES
// ============================================================================

export interface TriggerFilters extends PaginationParams {
  category?: 'sensor' | 'forecast';
  enabled?: boolean;
  companyId?: string; // Solo superadmin puede filtrar por empresa
}

// ============================================================================
// TRIGGERS SERVICE
// ============================================================================

export const triggersService = {
  /**
   * Listar triggers
   */
  async getAll(params?: TriggerFilters): Promise<PaginatedResponse<ApiTrigger>> {
    return api.getPaginated<ApiTrigger>('/triggers', params);
  },

  /**
   * Obtener trigger por ID
   */
  async getById(id: string): Promise<ApiResponse<ApiTrigger>> {
    return api.get<ApiTrigger>(`/triggers/${id}`);
  },

  /**
   * Crear nuevo trigger
   */
  async create(data: CreateTriggerRequest): Promise<ApiResponse<ApiTrigger>> {
    return api.post<ApiTrigger>('/triggers', data);
  },

  /**
   * Actualizar trigger
   */
  async update(id: string, data: UpdateTriggerRequest): Promise<ApiResponse<ApiTrigger>> {
    return api.put<ApiTrigger>(`/triggers/${id}`, data);
  },

  /**
   * Activar/desactivar trigger
   */
  async toggle(id: string, enabled?: boolean): Promise<ApiResponse<ApiTrigger>> {
    const body = enabled !== undefined ? { enabled } : {};
    return api.put<ApiTrigger>(`/triggers/${id}/toggle`, body);
  },

  /**
   * Eliminar trigger
   */
  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/triggers/${id}`);
  },

  // ==========================================================================
  // ZONE ASSIGNMENTS (legacy – no usar para nuevos desarrollos)
  // ==========================================================================

  /**
   * Asignar trigger a zonas (legacy).
   * @deprecated Usar linkZones con zoneIds en su lugar.
   */
  async assignZones(triggerId: string, data: AssignTriggerZonesRequest): Promise<ApiResponse<ApiTrigger>> {
    return api.post<ApiTrigger>(`/triggers/${triggerId}/zones`, data);
  },

  /**
   * Eliminar asignación de zona (legacy).
   * @deprecated Usar unlinkZone(triggerId, zoneId) en su lugar.
   */
  async unassignZone(triggerId: string, assignmentId: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/triggers/${triggerId}/zones/${assignmentId}`);
  },

  // ==========================================================================
  // LINK-ZONES (v0.3 – usar estas)
  // ==========================================================================

  /**
   * Vincular trigger a zonas por IDs. Reemplaza la vinculación actual.
   * POST /triggers/:id/link-zones
   */
  async linkZones(triggerId: string, zoneIds: string[]): Promise<ApiResponse<ApiTrigger>> {
    return api.post<ApiTrigger>(`/triggers/${triggerId}/link-zones`, { zoneIds });
  },

  /**
   * Desvincular una zona del trigger.
   * DELETE /triggers/:id/link-zones/:zoneId
   */
  async unlinkZone(triggerId: string, zoneId: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/triggers/${triggerId}/link-zones/${zoneId}`);
  },

  /**
   * Obtener zonas vinculadas al trigger.
   * GET /triggers/:id/link-zones
   */
  async getLinkedZones(triggerId: string): Promise<ApiResponse<ApiTriggerLinkedZone[]>> {
    return api.get<ApiTriggerLinkedZone[]>(`/triggers/${triggerId}/link-zones`);
  },

  /**
   * Obtener controladoras afectadas por el trigger según las zonas vinculadas.
   * GET /triggers/:id/controllers
   */
  async getAffectedControllers(triggerId: string): Promise<ApiResponse<ApiAffectedControllersResponse>> {
    return api.get<ApiAffectedControllersResponse>(`/triggers/${triggerId}/controllers`);
  },
};

export default triggersService;
